<template>
  <!-- <ScreenSizeDebug /> -->
  <component :is="layoutComponent">
    <router-view />
  </component>

  <!-- Bug Tracker -->
  <BugTrackerBubble @action="handleBugTrackerAction" />
  <BugReportForm
    v-model:show="showBugReportForm"
    :pre-captured-screenshot="capturedScreenshot"
    @submitted="handleBugReportSubmitted"
  />
</template>

<script setup lang="ts">
import BugReportForm from '@/components/bug-tracker/BugReportForm.vue'
import BugTrackerBubble from '@/components/bug-tracker/BugTrackerBubble.vue'
import { ScreenshotService } from '@/services/screenshotService'
import { useAuthStore } from '@/stores/auth'
import DefaultLayout from '@layouts/DefaultLayout.vue'
import { computed, defineAsyncComponent, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
//import ScreenSizeDebug from '@components/Debug/ScreenSizeDebug.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const showBugReportForm = ref(false)
const capturedScreenshot = ref<{ dataUrl: string; blob: Blob; mimeType: string } | null>(null)
const isCapturingScreenshot = ref(false)

// Détermine dynamiquement le layout à utiliser
const layoutComponent = computed(() => {
  const layoutName = route.meta.layout as string | undefined
  if (layoutName) {
    // Charger dynamiquement le layout basé sur le nom
    // Assurez-vous que les noms correspondent aux fichiers dans src/layouts/
    // et sont en PascalCase (ex: 'DefaultLayout', 'ViewerLayout')
    return defineAsyncComponent(() => import(`@layouts/${layoutName}.vue`))
  }
  // Retourner le layout par défaut si aucun n'est spécifié
  return DefaultLayout
})

async function handleBugTrackerAction(action: string) {
  console.log('[BdC-App] Action bug tracker:', action)

  switch (action) {
    case 'report':
      // Capturer le screenshot AVANT d'ouvrir le formulaire
      await captureScreenshotAndOpenForm()
      break
    case 'list': {
      // Rediriger selon le rôle : admin vers /admin/bug-reports, researcher vers /researcher/bug-reports
      const userRole = authStore.userRole
      if (userRole === 'admin') {
        router.push('/admin/bug-reports')
      } else if (userRole === 'researcher') {
        router.push('/researcher/bug-reports')
      } else {
        console.warn('[BdC-App] Rôle non autorisé pour voir les remontées:', userRole)
      }
      break
    }
  }
}

async function captureScreenshotAndOpenForm() {
  isCapturingScreenshot.value = true
  try {
    console.log('[BdC-App] Capture screenshot avant ouverture formulaire...')

    // Capturer le screenshot en WebP (utilise getDisplayMedia par défaut)
    const screenshot = await ScreenshotService.capture({
      format: 'webp',
      maxWidth: 1920,
      maxHeight: 1080,
    })

    // Compresser si nécessaire (max 2MB)
    const compressedBlob = await ScreenshotService.compressImage(
      screenshot.blob,
      2 * 1024 * 1024,
      0.8
    )

    // Stocker le screenshot capturé
    capturedScreenshot.value = {
      dataUrl: screenshot.dataUrl,
      blob: compressedBlob,
      mimeType: screenshot.mimeType,
    }

    console.log('[BdC-App] Screenshot capturé, ouverture du formulaire')

    // Ouvrir le formulaire avec le screenshot déjà capturé
    showBugReportForm.value = true
  } catch (error) {
    console.warn('[BdC-App] Erreur capture screenshot (non bloquante):', error)
    // Ouvrir le formulaire quand même (screenshot optionnel)
    // Ne pas bloquer l'utilisateur - le screenshot est optionnel
    showBugReportForm.value = true
    capturedScreenshot.value = null
    // Ne pas afficher d'alerte pour ne pas bloquer l'utilisateur
  } finally {
    isCapturingScreenshot.value = false
  }
}

function handleBugReportSubmitted() {
  console.log('[BdC-App] Remontée soumise avec succès')
  // Réinitialiser le screenshot capturé
  capturedScreenshot.value = null
  // Optionnel: afficher une notification de succès
}
</script>

<style>
/* Les styles globaux sont dans main.css */
</style>
